import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import "./_style.scss";
import {
  ITKMutiTableHead,
  ITkDataTableBodyProps,
  ITkDataTableHeadePorps,
  ITkDataTableProps,
} from "../../../type/componentsType/TkDataTable";
import { useContext, useEffect, useMemo, useState } from "react";
import TkCheckbox from "../../atoms/TkCheckbox";
import TkTablePagination from "../../atoms/TkTablePagination";
import React from "react";
import TkTableContext, {
  TkTableContextProvider,
} from "../../../context/tkTableContext";

/**
 * 並び変える一覧表部品
 * headListとbodyListのpropsを使用して、テーブルコンポーネントをレンダリングします。
 * @param {Object} props - コンポーネントのprops。
 * @param {Array} props.headList - テーブルヘッダーセルのリスト。
 * @param {Array} props.bodyList - テーブルボディーセルのリスト。
 * @param {boolean} [checkSection=false] - 選択セクションを表示するかどうか。
 * @param {Function} onCheckSectionWidth - 選択セクションを取得するときのコールバック関数。
 * @return TkDataTable 一覧表部品
 */
export const TkDataTable: React.FC<ITkDataTableProps> = React.memo((props) => {
  return (
    <TkTableContextProvider>
      <TkDataTableRef {...props} />
    </TkTableContextProvider>
  );
});

export const TkDataTableRef: React.FC<ITkDataTableProps> = (props) => {
  const {
    headList,
    bodyList,
    checkSection = false,
    onCheckSectionWidth,
    defaultRowsPerPage,
    pagin = false,
    count,
    onPageChange,
    onGetPaginList,
    onSortChange,
    headerLeftComponent,
    showUnit = false,
    unitText = "金額単位(円)",
    wantToCheck,
    useScroll = false,
    checkboxIndex = 0,
    dontResetPage = false,
    setPageValue,
    checkedAll
  } = props;

  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [sortOn, setSortOn] = useState<number | null>(null);
  const [sortedBodyList, setSortedBodyList] = useState(bodyList);
  const [showBody, setShowBody] = useState<any>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isMultiHead, setIsMultiHead] = useState(false);
  const [selectAllDisabled, setSelectAllDisabled] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const { tableContext, setTableContext } = useContext(TkTableContext);

  useEffect(() => {
    setPageValue && setPage(setPageValue);
    setTableContext({
      ...tableContext,
      pageInContext: setPageValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPageValue]);

  useEffect(() => {
    const validRowsPerPage = tableContext.rowsPerPageInContext;
    setPage(tableContext.pageInContext ?? 0);
    setRowsPerPage(validRowsPerPage as 5 | 10 | 25 | undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableContext.pageInContext, tableContext.rowsPerPageInContext]);

  const pageCont = useMemo(() => {
    return bodyList?.length ? bodyList.length : count;
  }, [count, bodyList]);

  const formatHeadList: (ITkDataTableHeadePorps | ITkDataTableHeadePorps[])[] =
    useMemo(() => {
      let globalIndex = 0;
      return (headList as ITKMutiTableHead[]).map((item) => {
        if ("top" in item && item.top && item.bottom) {
          const result = [
            {
              headTitle: item.top.headTitle,
              ...item.top,
            },
            ...item.bottom.map((b) => ({
              headTitle: b.headTitle,
              index: globalIndex++,
              ...b,
            })),
          ];
          return result;
        } else {
          return {
            headTitle: item.headTitle,
            index: globalIndex++,
            ...item,
          };
        }
      });
    }, [headList]);

  // ソート処理
  useEffect(() => {
    setSortedBodyList(bodyList);

    if (!pagin) {
      setShowBody(bodyList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyList, pagin]);

  // マルチヘッダーの判定
  useEffect(() => {
    headList?.forEach((item: ITkDataTableHeadePorps) => {
      if ("top" in item) {
        setIsMultiHead(true);
      }
    });
  }, [headList]);

  // 選択セクションを取得する
  useEffect(() => {
    onCheckSectionWidth && onCheckSectionWidth(selected);

    const allSelectableSelected = bodyList?.every((item, index) => {
      if (!item[0]?.disabledCheckBox) {
        return selected.includes(index);
      }
      return true;
    });

    const allDisabledCheckBox = bodyList?.every(
      (item) => item[0]?.disabledCheckBox
    );

    setSelectAll((checkedAll && allSelectableSelected) || (allSelectableSelected && !allDisabledCheckBox));
    setSelectAllDisabled(allDisabledCheckBox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, bodyList, checkedAll]);

  useEffect(() => {
    const _selected = checkedAll ? bodyList?.map?.((e,idx) => idx) : wantToCheck || [];
    setSelected(_selected);
    setSelectAll(false);
  }, [bodyList, wantToCheck, checkedAll]);

  /**
   * 選択セクションを選択する
   * @param index - インデックス
   */
  const handleSelect = (item: any) => {
    const index = bodyList.findIndex((bodyItem) => bodyItem[0] === item);
    const isDisabled = bodyList[index][0]?.disabledCheckBox;
    if (isDisabled) {
      return; // Skip selection if disabledCheckBox is true
    }

    setSelected((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // 全ての選択セクションを選択する
  const handleSelectAll = () => {
    const selectableIndexes = bodyList
      .map((item, index) => (item[0].disabledCheckBox ? null : index))
      .filter((index) => index !== null) as number[];
    if (selectAll) {
      setSelected([]);
      const finalSelectedIndexes = wantToCheck?.filter(
        (checkIndex: any) => bodyList[checkIndex][0].disabledCheckBox
      );
      setSelected(finalSelectedIndexes ?? []);
    } else {
      const finalSelectedIndexes = [...selectableIndexes];
      wantToCheck?.forEach((checkIndex: any) => {
        if (bodyList[checkIndex][0].disabledCheckBox) {
          finalSelectedIndexes.push(checkIndex);
        }
      });
      setSelected(finalSelectedIndexes);
    }
    setSelectAll(!selectAll);
  };
  /**
   * ページングリストを取得する
   * @param paginList - ページングリスト
   */
  const handleOnGetPaginList = (paginList: any) => {
    setShowBody(paginList);
    setSelected(wantToCheck || []);
    setSelectAll(false);
    onGetPaginList && onGetPaginList(paginList);
  };

  /**
   * ソートを処理する関数
   * @param index - ソートする列のインデックス
   */
  const handleSort = (index: number) => {
    if (bodyList && bodyList.length === 0) return;

    setOrder(order === "asc" ? "desc" : "asc");
    setSortOn(index);

    const sorted = [...bodyList].sort((a, b) => {
      if (index >= a.length) index = a.length - 1;
      if (a[index].children === undefined || b[index].children === undefined) {
        a[index].id && index++;
      }

      const aValue = a[index].forSort;
      const bValue = b[index].forSort;

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return order === "asc" ? -1 : 1;
      if (bValue === null) return order === "asc" ? 1 : -1;

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    if (!onSortChange) {
      if (!pagin) {
        setShowBody(sorted);
      }
      setSortedBodyList([...sorted]);
    }
    setSelected([]);
    setSelectAll(false);
    onSortChange && onSortChange(sorted);
  };

  const TkTableCell = React.memo((props: any) => {
    /**
     * テーブルセルのプロパティを定義します。
     * @typedef {Object} TableCellProps
     * @property {string} headTitle - テーブルセルのタイトル。
     * @property {string} [align="center"] - テーブルセルのコンテンツの配置。
     * @property {string} [className] - テーブルセルのCSSクラス名。
     * @property {string} [verticalAlign] - テーブルセルのコンテンツの垂直方向の配置。
     * @property {number} [rowSpan] - テーブルセルの行のスパン。
     * @property {number} [colSpan] - テーブルセルの列のスパン。
     * @property {boolean} [dontNeedSort] - ソートを不要にする。
     * @property {number} index - ソート用インデックス。
     */
    const {
      headTitle,
      align = "center",
      verticalAlign,
      className,
      index,
    } = props;
    return (
      <TableCell
        style={{
          verticalAlign: verticalAlign,
        }}
        className={`tableCell ${className}`}
        align={align}
        rowSpan={props.rowSpan || 1}
        colSpan={props.colSpan || 1}
      >
        {props.dontNeedSort ? (
          <>{headTitle}</>
        ) : (
          <TableSortLabel
            active={sortOn === index}
            direction={order}
            onClick={() => handleSort(index)}
          >
            {headTitle}
          </TableSortLabel>
        )}
      </TableCell>
    );
  });

  const TkCheckSection = () => {
    return (
      <>
        {checkSection && (
          <TableCell
            align="center"
            className={`tableCell`}
            rowSpan={isMultiHead ? 2 : 1}
          >
            <TkCheckbox
              label="選択"
              checked={selectAll}
              onChange={handleSelectAll}
              disabled={selectAllDisabled || bodyList?.length === 0}
            />
          </TableCell>
        )}
      </>
    );
  };

  return (
    <Box className="tkDataTableMain">
      <Box className="tkDataTableHeader">
        <Box className="tkDataTableHeaderLeft">{headerLeftComponent}</Box>

        <Box className="tkDataTableHeaderPagin tkDataTableHeaderPaginTop">
          {pagin && (
            <TkTablePagination
              count={pageCont}
              paginList={sortedBodyList}
              dontResetPage={dontResetPage}
              onPageChange={onPageChange}
              onGetPaginList={handleOnGetPaginList}
              defaultRowsPerPage={rowsPerPage}
              pageInProps={page}
            />
          )}
        </Box>

        <Box className="tkDataTableHeaderRight">
          {showUnit && (
            <div dangerouslySetInnerHTML={{ __html: unitText }}></div>
          )}
        </Box>
      </Box>
      <TableContainer
        className={`tkDataTable ${useScroll && "tkDataTableCanScroll"}`}
      >
        <Table className={`tkDataTable`}>
          <TableHead className="tkDataTableHead">
            {isMultiHead ? (
              <>
                <TableRow>
                  {formatHeadList?.map(
                    (
                      item: ITkDataTableHeadePorps | ITkDataTableHeadePorps[],
                      index: number
                    ) => {
                      return (
                        <React.Fragment key={`top-${index}`}>
                          {index === checkboxIndex && <TkCheckSection />}
                          {Array.isArray(item) ? (
                            <TkTableCell
                              {...item[0]}
                              dontNeedSort
                              colSpan={item.length - 1}
                            />
                          ) : (
                            <TkTableCell {...item} rowSpan={2} />
                          )}
                        </React.Fragment>
                      );
                    }
                  )}
                </TableRow>
                <TableRow>
                  {formatHeadList?.map(
                    (
                      item: ITkDataTableHeadePorps | ITkDataTableHeadePorps[],
                      index: number
                    ) => {
                      return (
                        <React.Fragment key={`bottom-${index}`}>
                          {Array.isArray(item) &&
                            item.map(
                              (i: ITkDataTableHeadePorps, index: number) => {
                                if (index === 0) return null;
                                return (
                                  <React.Fragment key={`bottom-index-${index}`}>
                                    <TkTableCell {...i} />
                                  </React.Fragment>
                                );
                              }
                            )}
                        </React.Fragment>
                      );
                    }
                  )}
                </TableRow>
              </>
            ) : (
              <TableRow>
                {headList?.map(
                  (item: ITkDataTableHeadePorps, index: number) => {
                    item.index = index;
                    return (
                      <React.Fragment key={index}>
                        {index === checkboxIndex && <TkCheckSection />}
                        <TkTableCell {...item} />
                      </React.Fragment>
                    );
                  }
                )}
              </TableRow>
            )}
          </TableHead>

          <TableBody className="tkDataTableBody">
            {showBody?.length ? (
              showBody?.map(
                (bodyItem: ITkDataTableBodyProps[], index: number) => {
                  return (
                    <TableRow className="tableRow" key={index}>
                      {bodyItem.map(
                        (item: ITkDataTableBodyProps, index: number) => {
                          if (item.id) return null;
                          /**
                           * TkDataTableコンポーネントは、表形式のデータを表示するために使用されます。
                           *
                           * @param children - TkDataTable内に表示されるReact要素。
                           * @param align - TkDataTable内のセルのテキストの水平方向の配置。"left"、"center"、または"right"のいずれかを指定できます。
                           * @param className - TkDataTableに適用されるCSSクラス名。
                           * @param cellWidth - TkDataTable内の各セルの幅を指定するための数値。headListの長さに基づいて計算されます。
                           */
                          const {
                            children,
                            align,
                            verticalAlign,
                            className,
                            width,
                            backgroundColor,
                          } = item;

                          return (
                            <React.Fragment key={index}>
                              {checkSection && index === checkboxIndex && (
                                <TableCell
                                  style={{
                                    verticalAlign: "middle",
                                    wordWrap: "break-word",
                                    wordBreak: "break-all",
                                  }}
                                  width={100}
                                  className={`tableCell noMargin`}
                                  align="center"
                                  key={"check-" + index}
                                  data-testid="tkDataTableBodyCellForTest"
                                >
                                  <TkCheckbox
                                    checked={selected.includes(
                                      bodyList.findIndex(
                                        (item) => item[0] === bodyItem[0]
                                      )
                                    )}
                                    onChange={() => handleSelect(bodyItem[0])}
                                    disabled={bodyItem[0].disabledCheckBox}
                                  />
                                </TableCell>
                              )}
                              <TableCell
                                style={{
                                  verticalAlign: verticalAlign,
                                  wordWrap: "break-word",
                                  wordBreak: "break-all",
                                  backgroundColor: backgroundColor,
                                }}
                                width={width ? `${width}` : ""}
                                className={`tableCell ${className}`}
                                align={align}
                                key={"value-" + index}
                                data-testid="tkDataTableBodyCellForTest"
                              >
                                {children}
                              </TableCell>
                            </React.Fragment>
                          );
                        }
                      )}
                    </TableRow>
                  );
                }
              )
            ) : (
              <TableRow>
                <TableCell
                  className="tableCell"
                  align="center"
                  colSpan={headList?.length + (checkSection ? 1 : 0)}
                >
                  データがありません。
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box className="tkDataTableHeader">
        <Box className="tkDataTableHeaderLeft"></Box>

        <Box className="tkDataTableHeaderPagin">
          {pagin && (
            <TkTablePagination
              count={pageCont}
              paginList={sortedBodyList}
              dontResetPage={dontResetPage}
              onPageChange={onPageChange}
              onGetPaginList={handleOnGetPaginList}
              defaultRowsPerPage={rowsPerPage}
              pageInProps={page}
            />
          )}
        </Box>

        <Box className="tkDataTableHeaderRight"></Box>
      </Box>
    </Box>
  );
};

export default TkDataTable;
